'use strict'

import { isUrl } from './utils'
import store from './../store/'
var AudioPlayer = false

/**
 * Ensure that the audioPlayer is properly initialized.
 *  - We need it to be initialized by an user event, so we have
 *    the browser auth to play a sound when we want
 *  - More infos : https://github.com/scottschiller/SoundManager2/issues/178
 *
 * @param {Boolean} isTTSEnabled
 * @returns {Boolean}
 */
const ensureInit = isTTSEnabled => {
  try {
    // IE check
    if (typeof Audio === 'undefined') {
      AudioPlayer = false
    }

    if (AudioPlayer) {
      return false
    }

    AudioPlayer = new Audio()
    AudioPlayer.src = '' // Playing, even an inexistant file, unlock the auth

    /*
     * play() returns a Promise which is resolved when playback has been successfully started.
     * Failure to begin playback for any reason, such as permission issues, result in the promise being rejected.
     * Note: Older browsers may not return a value from play().
     * As the src is empty, it will always throw an error
     */
    const playPromise = AudioPlayer.play()
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // The play failed because the src is empty, but we have the auth
        if (error.code === 9) {
          return true
        }

        console.info('AudioPlayer init failed: ' + error.message)
        AudioPlayer = false
        return false
      })
    }

    // If the Text to Speach is activated, let's set the activated icon
    if (isTTSEnabled) {
      setVoiceClass(false, true, true) // Set the icon to activated not speaking
    }

    AudioPlayer.addEventListener('ended', event => {
      setVoiceClass(false, true, true) // Set the icon to activated not speaking
    })
    AudioPlayer.addEventListener('error', event => {
      setVoiceClass(false, true, true) // Set the icon to activated not speaking
    })
  } catch (err) {
    console.error('Audio ensureInit error', err)
  }
}

/**
 * Check if an string is a valid mp3 url
 *
 * @param {String} mp3Url - Url of the mp3 file
 * @returns {Boolean}
 */
const isMp3Url = mp3Url => {
  return isUrl(mp3Url) && mp3Url.endsWith('.mp3')
}

/**
 * Start the playback of a mp3 file
 *
 * @param {String} mp3Url - Url of the mp3 file
 * @returns {Boolean}
 */
const playMp3 = mp3Url => {
  try {
    if (!AudioPlayer || !isMp3Url(mp3Url)) {
      return false
    }

    stop(true) // isTTSEnabled is true because if we go for playing MP3, isTTSEnabled was obviously true
    AudioPlayer.src = mp3Url
    AudioPlayer.load()
    setVoiceClass(true, true, true) // Set the icon to speaking

    /*
     * play() returns a Promise which is resolved when playback has been successfully started.
     * Failure to begin playback for any reason, such as permission issues, result in the promise being rejected.
     * Note: Older browsers may not return a value from play().
     */
    const playPromise = AudioPlayer.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        setVoiceClass(false, true, true) // Set the icon to activated not speaking
        return false
      })
    }
  } catch (err) {
    store.dispatch('set_audio_playing', false)
    console.error('Audio playMp3 error', err)
  }

  return true
}

/**
 * Stop the playback of a mp3
 * @param {Boolean} isTTSEnabled
 */
const stop = isTTSEnabled => {
  try {
    if (!AudioPlayer) {
      return false
    }

    AudioPlayer.pause()
    AudioPlayer.currentTime = 0.0

    // Set the icon to activated not speaking or deactivated
    setVoiceClass(false, isTTSEnabled, true)
  } catch (err) {
    store.dispatch('set_audio_playing', false)
    console.error('Audio stop error', err)
  }

  return true
}

/**
 * Set voice icon depending on voice status
 * @param {Boolean} isSpeaking
 * @param {Boolean} isVoiceActived
 * @param {Boolean} isVoiceOn
 */
const setVoiceClass = (isSpeaking, isVoiceActived = true, isVoiceOn = true) => {
  const voiceSpeakingIcon = document.getElementById('voice-speaking')
  const voiceActivatedIcon = document.getElementById('voice-activated')
  const voiceDeactivatedIcon = document.getElementById('voice-deactivated')

  if (!voiceSpeakingIcon || !voiceActivatedIcon || !voiceDeactivatedIcon) {
    return false
  }

  store.dispatch('set_audio_playing', isSpeaking)

  voiceActivatedIcon.classList.remove('active')
  voiceSpeakingIcon.classList.remove('active')
  voiceDeactivatedIcon.classList.remove('active')

  if (!isVoiceOn) {
    return
  }

  if (isSpeaking) {
    voiceSpeakingIcon.classList.add('active')
  } else if (!isVoiceActived) {
    voiceDeactivatedIcon.classList.add('active')
  } else {
    voiceActivatedIcon.classList.add('active')
  }
}

/**
 * Check microphone permission given in feature-policy header
 * @returns {Boolean}
 */
const checkMicrophoneHeaderPermissionGiven = () => {
  return document.featurePolicy && typeof document.featurePolicy.allowsFeature === 'function'
    ? document.featurePolicy.allowsFeature('microphone')
    : true
}

export default {
  ensureInit,
  playMp3,
  setVoiceClass,
  stop,
  checkMicrophoneHeaderPermissionGiven
}
